/**
 * Quick task 260819-kq3 — `#` task references in comments & descriptions.
 *
 * Unlike @mentions (Phase 25 D-04), task references need NO hidden encoding:
 * ticket codes are stable, human-readable identifiers (`${project.slug}-${seq}`,
 * e.g. `MKS-138`) resolved case-insensitively by the MKS-144 locate endpoint.
 * The stored markdown is the plain-text token itself (`#MKS-138`), so manually
 * typed references in pre-existing comments linkify retroactively.
 *
 * Rendering happens through `remarkTaskRefs`, a remark plugin that splits
 * mdast text nodes around token matches into `link` nodes pointing at the
 * shareable full-page ticket route `/tasks/:taskRef`. Operating on the mdast
 * tree (rather than preprocessing the raw string) means tokens inside inline
 * code, fenced blocks, and existing links are never touched.
 */

/**
 * Token pattern: `#` + letter-prefixed slug (letters/digits/underscore/dash)
 * + `-` + numeric sequence. Guards:
 *  - `(?<![\w#])` — not preceded by a word char or another `#` (so `abc#X-1`
 *    and `##X-1` don't linkify; URLs with fragments become autolinked `link`
 *    nodes and are skipped at the tree level anyway).
 *  - `(?![\w-])` — token must end at the numeric sequence (`#MKS-138-x`
 *    stays plain text rather than half-linkifying).
 * Markdown headings never collide: ATX headings require a space after `#`.
 */
const TASK_REF_SOURCE = '(?<![\\w#])#([A-Za-z][\\w-]*-\\d+)(?![\\w-])';

/** Fresh global regex per call — avoids shared-`lastIndex` bugs. */
export function taskRefRegex(): RegExp {
  return new RegExp(TASK_REF_SOURCE, 'g');
}

/** Href prefix the MarkdownBody chip renderer keys on. */
export const TASK_REF_HREF_PREFIX = '/tasks/';

/** Build the app-route href for a ticket code. */
export function taskRefHref(code: string): string {
  return `${TASK_REF_HREF_PREFIX}${encodeURIComponent(code)}`;
}

// ---------------------------------------------------------------------------
// remark plugin
// ---------------------------------------------------------------------------

/** Minimal structural mdast typing — keeps us off `@types/mdast` internals. */
interface MdNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdNode[];
}

/**
 * Split a single text node's value into text + link nodes around task-ref
 * matches. Returns null when the value contains no token (hot path — most
 * text nodes are untouched).
 */
function splitTextNode(value: string): MdNode[] | null {
  const re = taskRefRegex();
  let match = re.exec(value);
  if (!match) return null;

  const out: MdNode[] = [];
  let cursor = 0;
  while (match) {
    if (match.index > cursor) {
      out.push({ type: 'text', value: value.slice(cursor, match.index) });
    }
    // Group 1 always exists when the pattern matches; '' fallback is for the
    // type system only (noUncheckedIndexedAccess).
    const code = match[1] ?? '';
    out.push({
      type: 'link',
      url: taskRefHref(code),
      children: [{ type: 'text', value: `#${code}` }],
    });
    cursor = match.index + match[0].length;
    match = re.exec(value);
  }
  if (cursor < value.length) {
    out.push({ type: 'text', value: value.slice(cursor) });
  }
  return out;
}

/**
 * Remark plugin: walk every parent node (skipping `link`/`linkReference`
 * subtrees so we never nest links) and replace matching text children.
 * Code nodes need no special-casing — `code`/`inlineCode` carry their content
 * in `value`, not in text children, so the walker never sees them as text.
 */
export function remarkTaskRefs() {
  return function transform(tree: MdNode): void {
    function walk(node: MdNode): void {
      if (!node.children) return;
      if (node.type === 'link' || node.type === 'linkReference') return;
      const next: MdNode[] = [];
      let changed = false;
      for (const child of node.children) {
        if (child.type === 'text' && typeof child.value === 'string') {
          const split = splitTextNode(child.value);
          if (split) {
            next.push(...split);
            changed = true;
            continue;
          }
        }
        walk(child);
        next.push(child);
      }
      if (changed) node.children = next;
    }
    walk(tree);
  };
}
